import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { GenericFormPage } from '../../../components/generic';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
  type AdminUserFormModel,
} from './config';
import { useUser, User } from './useData';

function useAdminUserForm(id: number) {
  const r = useUser(id);
  return {
    ...r,
    data: r.data as AdminUserFormModel | undefined,
    user: r.user as AdminUserFormModel | undefined,
  };
}

const Form: React.FC = () => {
  const location = useLocation();
  const isEditMode = location.pathname.includes('/edit');
  
  // Update the password field - required for create, optional for edit
  const configWithPasswordHandling = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'userPassword') {
        return {
          ...field,
          required: !isEditMode, // Required only for create, optional for edit
          placeholder: isEditMode ? 'Leave blank to keep current password' : 'Enter password',
          validation: isEditMode ? {
            ...field.validation,
            required: false, // Make optional in edit mode
          } : field.validation,
        };
      }
      return field;
    });

    // Custom create/update handlers that handle password correctly
    const customCreate = async (data: Partial<AdminUserFormModel>) => {
      return await moduleConfig.form.create(data as Partial<User & { userPassword?: string }>);
    };

    const customUpdate = async (id: number, data: Partial<AdminUserFormModel>) => {
      // Remove password if it's empty (don't update password if not provided)
      const updateData = { ...data };
      if (updateData.userPassword === '' || updateData.userPassword === undefined || updateData.userPassword === null) {
        delete updateData.userPassword;
      }
      return await moduleConfig.form.update(id, updateData as Partial<User & { userPassword?: string }>);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData,
      mapItemToFormData,
      form: {
        ...moduleConfig.form,
        create: customCreate,
        update: customUpdate,
      },
    };
  }, [isEditMode]);

  return (
    <GenericFormPage<AdminUserFormModel>
      useData={useAdminUserForm}
      config={configWithPasswordHandling}
    />
  );
};

export default Form;

